import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import type { ExpensePartyRecord } from "@/lib/expenses/expensePartiesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ExpensePartySelectProps = {
  value: string;
  parties: ExpensePartyRecord[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

function itemValue(id: string): string {
  return `party:${id}`;
}

export function ExpensePartySelect({
  value,
  parties,
  onChange,
  placeholder = "Search party",
  triggerClassName,
  disabled = false,
}: ExpensePartySelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);
  const [highlight, setHighlight] = useState("");

  const selected = useMemo(
    () => parties.find((party) => party.id === value) ?? null,
    [parties, value],
  );

  useEffect(() => {
    if (!open) return;
    setHighlight(value ? itemValue(value) : "");
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-expense-party-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setHighlight(value ? itemValue(value) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">
            {selected ? selected.name || `Party #${selected.id}` : placeholder}
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, query, keywords) => {
            const q = query.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${(keywords ?? []).join(" ")} ${item}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput {...commandInputProps} placeholder="Search party..." className="h-9 text-xs" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              {parties.length === 0 ? "No parties found." : "No party found."}
            </CommandEmpty>
            <CommandGroup>
              {parties.map((party) => {
                const isSelected = party.id === value;
                const label = party.name || `Party #${party.id}`;
                return (
                  <CommandItem
                    key={party.id}
                    value={itemValue(party.id)}
                    keywords={[party.name ?? "", party.id]}
                    data-expense-party-selected={isSelected ? "true" : undefined}
                    onSelect={() => {
                      onChange(party.id);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="break-words font-medium truncate">{label}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
